import React, { useState } from "react";
import { StepInputControlModel } from "../../../models/step-input.model";
import StepInput from "./step-input.component";

interface Props extends StepInputControlModel {
    handleOnChangeStepValue: (str: string) => any;
    setWidth?: string;
    OtpTextFieldColor?: string;
}

const StepInputControl: React.FC<Props> = (props) => {
    const [activeInput, setActiveInput] = useState(0);
    const [stepValues, setStepValues] = useState(Array<string>(props.numOfSteps).fill(""));

    const handleStepValueChange = (str: string[]) => {
        const stepValue = str.join("");
        props.handleOnChangeStepValue(stepValue);
    };

    const getChangedValue = (str: string) => {
        return !props.isNumberInput || !str || /\d/.test(str) ? str : "";
    };

    const handleValueAtFocus = (str: string) => {
        const updatedStepValues = [...stepValues];
        updatedStepValues[activeInput] = str || "";
        setStepValues(updatedStepValues);
        handleStepValueChange(updatedStepValues);
    };

    const handleInput = (inputIndex: number) => {
        const selectedIndex = Math.max(
            Math.min(props.numOfSteps - 1, inputIndex),
            0
        );
        setActiveInput(selectedIndex);
    };

    const handlePrevInput = () => {
        handleInput(activeInput - 1);
    };

    const handleNextInput = () => {
        handleInput(activeInput + 1);
    };

    const handleOnFocus = (index: number) => () => {
        handleInput(index);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = getChangedValue(e.currentTarget.value);
        if (val.length >= props.stepLength) {
            handleValueAtFocus(val);
            handleNextInput();
        }
    };

    const handleOnBlur = () => {
        setActiveInput(-1);
    };

    const handleBackspace = (str: string, len: number) => {
        const updatedStepValues = [...stepValues];
        updatedStepValues[activeInput] = str.substr(0, len) || "";
        setStepValues(updatedStepValues);
        handleStepValueChange(updatedStepValues);
    };

    const handleDelete = (str: string, len: number) => {
        const updatedStepValues = [...stepValues];
        let val = str.split("");
        val.splice(len, 1);
        const updatedVal = val.join("");
        updatedStepValues[activeInput] = updatedVal || "";
        setStepValues(updatedStepValues);
        handleStepValueChange(updatedStepValues);
    };

    const handleOnKeyUp = (e: any) => {
        let target: any = e.target;
        switch (e.keyCode) {
            case 46: {
                if (!activeInput ? stepValues[activeInput].length === target.selectionStart : false) {
                    return false;
                } else if (stepValues[activeInput] || target.selectionStart === 0) {
                    handleDelete(stepValues[activeInput], target.selectionStart);
                }
                break;
            }
            case 8: {
                if (!activeInput ? stepValues[activeInput].length === 0 : "" && activeInput === 0 && target.selectionStart !== 0) {
                    return false;
                } else if (stepValues[activeInput]) {
                    handleBackspace(stepValues[activeInput], stepValues[activeInput].length - 1);
                    handlePrevInput();
                } else {
                    handlePrevInput();
                }
                break;
            }
            case 37: {
                if (target.selectionStart === 0) {
                    e.preventDefault();
                    handlePrevInput();
                }
                break;
            }
            case 39: {
                if (target.selectionStart === target.maxLength) {
                    e.preventDefault();
                    handleNextInput();
                }
                break;
            }
            case 0: {
                e.preventDefault();
                break;
            }
            default:
                break;
        }
    };

    const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const isPasteData = e.clipboardData.getData("text/plain").trim().split("");
        if (!!isPasteData) {
            setStepValues([]);
        }
    };

    return (
        <>
            {Array(props.numOfSteps)
                .fill("")
                .map((_, index) => (
                    <StepInput
                        key={index}
                        index={index}
                        numOfSteps={props.numOfSteps}
                        focus={activeInput === index}
                        value={stepValues && stepValues[index]}
                        onFocus={handleOnFocus(index)}
                        onChange={handleOnChange}
                        onKeyUp={handleOnKeyUp}
                        onBlur={handleOnBlur}
                        onPaste={handleOnPaste}
                        disabled={props.disabled}
                        stepLength={props.stepLength}
                        setWidth={props.setWidth}
                        OtpTextFieldColor={props.OtpTextFieldColor}
                    />
                ))}
        </>
    );
};

export default StepInputControl;
